import type { ExpanderListItem } from '@rio-cloud/rio-uikit/ExpanderList';

import { BulkMaterialUnitDetails } from '#components/tourstopdetails/BulkMaterialUnitDetails.tsx';
import { CustomerTrailerUnitDetails } from '#components/tourstopdetails/CustomerTrailerUnitDetails.tsx';
import { HandlingUnitDetails } from '#components/tourstopdetails/HandlingUnitDetails.tsx';
import { SwapBodyUnitDetails } from '#components/tourstopdetails/SwapBodyUnitDetails.tsx';
import type { Goods } from '#domain/model/Goods.ts';
import { hasTransportOrderDeviation } from '#domain/model/hasDeviation.ts';
import type { StopType } from '#domain/model/Stop.ts';
import type { TransportOrder } from '#domain/model/TransportOrder.ts';

const getIcon = (type?: Goods['type']) => {
    switch (type) {
        case 'cargo-material':
            return 'rioglyph rioglyph-palette';
        case 'bulk-material':
            return 'rioglyph rioglyph-trailer-tipping';
        case 'swap-body':
            return 'rioglyph rioglyph-cargo';
        case 'customer-trailer':
            return 'rioglyph rioglyph-trailer';
        default:
            return 'rioglyph rioglyph-palette';
    }
};

export const getTransportOrderExpanderListItem = ({
    stopType,
    transportOrder,
    index,
    isSelected,
    setSelectedIndex,
}: {
    stopType: StopType;
    transportOrder: TransportOrder;
    index: number;
    isSelected: boolean;
    setSelectedIndex: (stopId?: number) => void;
}): ExpanderListItem => {
    const note =
        stopType === 'load'
            ? transportOrder.loading?.note
            : stopType === 'unload'
              ? transportOrder.unloading?.note
              : undefined;
    return {
        id: transportOrder.id,
        header: (
            <div>
                <span
                    className={`${getIcon(transportOrder.goods?.type)} text-color-${hasTransportOrderDeviation(stopType, transportOrder) ? 'danger' : 'gray'} text-size-h4 padding-right-10`}
                />
                <span>{transportOrder.reference_id} </span>
            </div>
        ),
        open: isSelected,
        onOpen: () => setSelectedIndex(index),
        onClose: () => setSelectedIndex(undefined),
        bodyClassName: 'padding-top-0',
        body: transportOrder.goods ? (
            <div className='display-flex flex-column gap-5'>
                <span>{transportOrder.secondary_reference_id}</span>
                {note && <span>{note}</span>}
                {getBody(transportOrder.goods)}
            </div>
        ) : null,
    };
};

const getBody = (goods: Goods) => {
    switch (goods.type) {
        case 'cargo-material':
            return goods.units.map(unit => <HandlingUnitDetails key={unit.id} unit={unit} />);
        case 'bulk-material':
            return goods.units.map(unit => <BulkMaterialUnitDetails key={unit.id} unit={unit} />);
        case 'swap-body':
            return goods.units.map(unit => <SwapBodyUnitDetails key={unit.id} unit={unit} />);
        case 'customer-trailer':
            return goods.units.map(unit => <CustomerTrailerUnitDetails key={unit.id} unit={unit} />);
    }
};
